import * as Sentry from "@sentry/react";
import { isChunkLoadError, CHUNK_RELOAD_SESSION_KEY } from "@/lib/utils/chunk-load";

/**
 * Reload once to pick up a freshly deployed build after a dynamic import fails.
 * Shares CHUNK_RELOAD_SESSION_KEY with the router error boundary so the two
 * recovery paths never reload-loop against each other. Returns true if a reload
 * was triggered.
 */
function reloadOnceForStaleChunk(scope: string): boolean {
  let alreadyTried = false;
  try {
    alreadyTried =
      typeof sessionStorage !== "undefined" &&
      !!sessionStorage.getItem(CHUNK_RELOAD_SESSION_KEY);
  } catch {
    alreadyTried = false;
  }
  if (alreadyTried) return false;

  Sentry.addBreadcrumb({
    category: "navigation",
    level: "info",
    message: "chunk_load_auto_reload",
    data: { scope },
  });
  try {
    sessionStorage.setItem(CHUNK_RELOAD_SESSION_KEY, Date.now().toString());
  } catch {
    /* ignore */
  }
  window.location.reload();
  return true;
}

/**
 * Install global listeners that recover from dynamic-import failures outside the
 * React Router error boundary: Vite's `vite:preloadError` plus uncaught
 * chunk-load errors and promise rejections. Without these, a stale chunk after a
 * deploy surfaces as a broken page instead of self-healing.
 */
export function installChunkReloadHandler(): void {
  if (typeof window === "undefined") return;

  window.addEventListener("vite:preloadError", (event) => {
    if (reloadOnceForStaleChunk("preload")) event.preventDefault();
  });

  window.addEventListener("error", (event) => {
    if (isChunkLoadError(event.error ?? event.message)) {
      reloadOnceForStaleChunk("window_error");
    }
  });

  window.addEventListener("unhandledrejection", (event) => {
    if (isChunkLoadError(event.reason)) {
      reloadOnceForStaleChunk("unhandled_rejection");
    }
  });
}
